import { type ApplicationError, type ForManagingTeams, type Team } from 'core'
import { z } from 'zod'

import { HTTPClient, HTTPMethods } from './http_client'
import type { TeamDTO } from './types'

export class TeamsManager implements ForManagingTeams {
	private readonly client = new HTTPClient()

	private formatTeam(team: TeamDTO): Team {
		return {
			id: team.id,
			name: team.name,
			createdAt: team.created_at,
			active: team.active
		}
	}

	async fetchTeams(): Promise<{
		data?: Team[]
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<{
			teams: TeamDTO[]
		}>('/authv2/teams/api/v1/teams')

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data?.teams.map(this.formatTeam) || []
		}
	}

	async fetchTeam(id: string): Promise<{
		data?: Team
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<TeamDTO>(
			`/authv2/teams/api/v1/teams/${id}`
		)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data ? this.formatTeam(response.data) : undefined
		}
	}

	async createTeam(
		params: z.infer<typeof ForManagingTeams.CreateTeam>
	): Promise<{
		data?: Team
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<
			TeamDTO,
			{
				name: string
			}
		>('/authv2/teams/api/v1/teams', {
			method: HTTPMethods.Post,
			data: {
				name: params.name
			}
		})

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data ? this.formatTeam(response.data) : undefined
		}
	}
}

export class TeamsManagerMock implements ForManagingTeams {
	data?: Team
	error?: ApplicationError

	async fetchTeams(): Promise<{
		data?: Team[]
		error?: ApplicationError
	}> {
		return {
			data: this.data ? [this.data] : [],
			error: this.error
		}
	}

	async fetchTeam(): Promise<{
		data?: Team
		error?: ApplicationError
	}> {
		return {
			data: this.data,
			error: this.error
		}
	}

	async createTeam(): Promise<{
		data?: Team
		error?: ApplicationError
	}> {
		return {
			data: this.data,
			error: this.error
		}
	}
}
